import { CircleAlertIcon, RefreshCwIcon, ScrollTextIcon, XIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EmptyState from "@/components/ui/empty-state";
import { Frame, FrameAction, FrameDescription, FrameFooter, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Stat, StatGrid } from "@/components/ui/stat";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toggle } from "@/components/ui/toggle";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { RelativeTime } from "@/components/dashboard_sourcing/RelativeTime";
import { ACCESS_HINT, ACCESS_TONE, STATUS_LABEL, STATUS_TONE, isApiKey, isChange } from "./format";
import { useMcpCalls } from "./useActivity";
import type { CallFilters, McpAccess, McpCallFacets, OpsMcpCall } from "./types";

/** Radix Select items cannot carry an empty value: this one stands for "no filter". */
const ALL = "__all__";

/** The Tool calls tab: the last 24 hours, who used the server, then the call log with its filters. */
export function CallsTab({
  filters,
  onFilter,
  onClear,
}: {
  filters: CallFilters;
  onFilter: (key: keyof CallFilters, value: string | null) => void;
  onClear: () => void;
}) {
  const feed = useMcpCalls(true, filters);
  const filtered = Boolean(filters.caller || filters.org_id || filters.tool || filters.status || filters.changes);
  const orgName = (orgId: string) => feed.orgNames[orgId] ?? orgId;

  return (
    <div className="flex flex-col gap-6">
      {feed.error && (
        <Alert variant="destructive">
          <AlertTitle>
            <CircleAlertIcon />
            Could not load the call log
          </AlertTitle>
          <AlertDescription>{feed.error}</AlertDescription>
        </Alert>
      )}

      {feed.facets ? (
        <>
          <LastDay facets={feed.facets} />
          <Callers facets={feed.facets} active={filters.caller} onPick={(caller) => onFilter("caller", caller === filters.caller ? null : caller)} />
        </>
      ) : (
        feed.loading && <Skeleton className="h-40 rounded-xl" />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Everyone"
          value={filters.caller}
          options={(feed.facets?.callers ?? []).map((c) => ({ value: c.caller, label: c.label }))}
          onChange={(v) => onFilter("caller", v)}
        />
        <FilterSelect
          label="Every client"
          value={filters.org_id}
          options={(feed.facets?.orgs ?? []).map((o) => ({ value: o.org_id, label: orgName(o.org_id) }))}
          onChange={(v) => onFilter("org_id", v)}
        />
        <FilterSelect
          label="Every tool"
          value={filters.tool}
          options={(feed.facets?.tools ?? []).map((t) => ({ value: t.tool, label: t.tool }))}
          onChange={(v) => onFilter("tool", v)}
          mono
        />
        <FilterSelect
          label="Any outcome"
          value={filters.status}
          options={(Object.keys(STATUS_LABEL) as OpsMcpCall["status"][]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
          onChange={(v) => onFilter("status", v)}
        />
        <Toggle variant="outline" size="sm" pressed={filters.changes} onPressedChange={(on) => onFilter("changes", on ? "1" : null)}>
          Changes only
        </Toggle>
        {filtered && (
          <Button variant="ghost" size="sm" onClick={onClear}>
            <XIcon />
            Clear
          </Button>
        )}
      </div>

      <Frame>
        <FrameHeader>
          <FrameTitle>Calls</FrameTitle>
          <FrameDescription>Newest first. Ids only: no names, addresses or email text are stored.</FrameDescription>
          <FrameAction>
            <Button variant="ghost" size="sm" onClick={() => void feed.refresh()} disabled={feed.loading}>
              {feed.loading ? <Spinner /> : <RefreshCwIcon />}
              Refresh
            </Button>
          </FrameAction>
        </FrameHeader>
        {feed.calls.length === 0 ? (
          <FramePanel>
            {feed.loading ? (
              <Skeleton className="h-24" />
            ) : (
              <EmptyState icon={<ScrollTextIcon />} text={filtered ? "No calls match these filters" : "No calls on the CS MCP server yet"} />
            )}
          </FramePanel>
        ) : (
          <CallsTable calls={feed.calls} orgName={orgName} />
        )}
        {feed.next && (
          <FrameFooter className="flex justify-center">
            <Button variant="secondary" size="sm" onClick={() => void feed.loadMore()} disabled={feed.loadingMore}>
              {feed.loadingMore && <Spinner />}
              Load older calls
            </Button>
          </FrameFooter>
        )}
      </Frame>
    </div>
  );
}

function LastDay({ facets }: { facets: McpCallFacets }) {
  const day = facets.last_24h;
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Last 24 hours</FrameTitle>
        <FrameDescription>Claude Code with the uplodio-ops plugin, and scripts with a key</FrameDescription>
      </FrameHeader>
      <StatGrid className="md:grid-cols-4">
        <Stat label="Calls" value={day.calls} highlight />
        <Stat label="Changes" value={day.changes} tooltip="Calls to tools that can change something: a decision filed, a campaign or plan edited, an email sent." />
        <Stat label="Errors" value={day.errors} tooltip="The tool failed, or the org was unknown to the caller." />
        <Stat label="People and keys" value={day.callers} />
      </StatGrid>
    </Frame>
  );
}

function AccessBadge({ access }: { access: McpAccess }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={ACCESS_TONE[access]} size="sm" className="cursor-help">
          {access}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{ACCESS_HINT[access]}</TooltipContent>
    </Tooltip>
  );
}

function Who({ caller, label, access }: { caller: string; label: string; access: McpAccess }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{label}</span>
      {isApiKey(caller) && <span className="shrink-0 text-xs text-muted-foreground">key</span>}
      <AccessBadge access={access} />
    </span>
  );
}

/** Who used the server in the window; a row filters the log to that caller. */
function Callers({ facets, active, onPick }: { facets: McpCallFacets; active: string | null; onPick: (caller: string) => void }) {
  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Who uses it</FrameTitle>
        <FrameDescription>
          Last {facets.window_days} days{facets.truncated ? ", newest 5,000 calls" : ""} · pick one to filter the log
        </FrameDescription>
      </FrameHeader>
      {facets.callers.length === 0 ? (
        <FramePanel className="text-sm text-muted-foreground">Nobody yet.</FramePanel>
      ) : (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead>Who</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Changes</TableHead>
              <TableHead className="text-right">Errors</TableHead>
              <TableHead>Last call</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {facets.callers.map((c) => (
              <TableRow
                key={c.caller}
                onClick={() => onPick(c.caller)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPick(c.caller);
                  }
                }}
                tabIndex={0}
                className={cn("cursor-pointer outline-none focus-visible:bg-muted/50", active === c.caller && "bg-muted/60")}
                aria-selected={active === c.caller}
              >
                <TableCell className="max-w-64">
                  <Who caller={c.caller} label={c.label} access={c.access} />
                </TableCell>
                <TableCell className="text-right font-mono">{c.calls}</TableCell>
                <TableCell className="text-right font-mono">{c.changes}</TableCell>
                <TableCell className={cn("text-right font-mono", c.errors > 0 && "text-destructive")}>{c.errors}</TableCell>
                <TableCell className="text-xs">
                  <RelativeTime at={c.last_at} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Frame>
  );
}

function CallsTable({ calls, orgName }: { calls: OpsMcpCall[]; orgName: (orgId: string) => string }) {
  return (
    <Table variant="card">
      <TableHeader>
        <TableRow>
          <TableHead>When</TableHead>
          <TableHead>Who</TableHead>
          <TableHead>Tool</TableHead>
          <TableHead>Client</TableHead>
          <TableHead>Outcome</TableHead>
          <TableHead>Ids</TableHead>
          <TableHead className="text-right">ms</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {calls.map((call) => (
          <TableRow key={call._id}>
            <TableCell className="text-xs whitespace-nowrap">
              <RelativeTime at={call.at} />
            </TableCell>
            <TableCell className="max-w-56">
              <Who caller={call.caller} label={call.caller_label} access={call.access} />
            </TableCell>
            <TableCell>
              <span className="flex items-center gap-1.5">
                <span className="font-mono text-xs">{call.tool}</span>
                {isChange(call) && (
                  <Badge variant="warning" size="sm">
                    change
                  </Badge>
                )}
              </span>
            </TableCell>
            <TableCell className="max-w-44 truncate">
              {call.org_id ? orgName(call.org_id) : call.ids.client ? <span className="font-mono text-xs">{call.ids.client}</span> : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell className="max-w-60 whitespace-normal">
              <Badge variant={STATUS_TONE[call.status]} size="sm">
                {STATUS_LABEL[call.status]}
              </Badge>
              {call.error && (
                <span className="mt-0.5 line-clamp-2 block text-xs text-destructive" title={call.error}>
                  {call.error}
                </span>
              )}
            </TableCell>
            <TableCell className="max-w-52 font-mono text-[11px] break-all whitespace-normal text-muted-foreground">
              {Object.entries(call.ids)
                .filter(([key]) => key !== "client" || call.org_id)
                .map(([key, value]) => `${key}=${value}`)
                .join(" ") || "—"}
            </TableCell>
            <TableCell className="text-right font-mono text-xs text-muted-foreground">{call.ms}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
  mono,
}: {
  label: string;
  value: string | null;
  options: { value: string; label: string }[];
  onChange: (value: string | null) => void;
  mono?: boolean;
}) {
  // A filter set from the URL may not be among this window's options: keep it selectable.
  const all = value && !options.some((o) => o.value === value) ? [{ value, label: value }, ...options] : options;
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
      <SelectTrigger size="sm" className={cn("w-44", value && "border-primary/40")}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}</SelectItem>
        {all.map((o) => (
          <SelectItem key={o.value} value={o.value} className={cn(mono && "font-mono text-xs")}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
